import React, { useState, useEffect, useCallback } from 'react';
import { FormField } from '../../uiwidgets/FormField';
import { FormContainer } from '../../uiwidgets/FormContainer';
import { SubmitButton } from '../../uiwidgets/SubmitButton';
import { CancelButton } from 'uiwidgets/CancelButton';
import { Row, Col, Card, Table, Button } from 'react-bootstrap';
import { API } from '../../api/API';
import { BUSINESS_ACCOUNT_TYPES_URL } from '../../config/config';
import { extractError } from '../../api/ErrorUtil';
import { doesStringHaveSpecialChars, isFullFormValid, 
    containsAlphaNumOnly, containsAlphaNumAndSpaceOnly } from 'util/Validators';
import _ from "lodash";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash } from '@fortawesome/free-solid-svg-icons'


const BusinessAccountTypesPage = () => {
    const initialState = {
        errors: {},
        infoMessage: "",
        accountTypeDisplayName: "",
        accountType: "",
        readOnly: false,
        editMode: false
    }

    const initialValidationState = {
        accountTypeDisplayNameError: false,
        accountTypeDisplayNameErrMsg: "",
        accountTypeError: false,
        accountTypeErrMsg: "",
    }

    const messageReset = () => {
        return {
            infoMessage: "",
            errors: {}
        }
    }

    const onToastClose = () => {
        setFormData({
            ...formData,
            ...messageReset()
        });
    }

    let [accountTypes, setAccountTypes] = useState([]);
    let [formData, setFormData] = useState(initialState);
    let [formValidityData, setFormValidityData] = useState(initialValidationState)

    const fetchAccountTypes = useCallback(async () => {
        try {
            let accType = await API.get(BUSINESS_ACCOUNT_TYPES_URL);
            setAccountTypes(accType.data);
        } catch (error) {
            let { message, errors, httpStatus } = extractError(error);
            if (httpStatus === 404) {
                setAccountTypes([]);
                setFormData({ ...formData, infoMessage: "No Account Types were found" });
            } else {
                setFormData({ ...formData, errors: { message, errors, httpStatus } });
            }
        }

    })

    useEffect(() => {
        fetchAccountTypes();
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        let formValid = isFullFormValid(formValidityData)

        if (!formValid) {
            alert("Form has validation errors");
            return false;
        }
        try {

            if (formData.editMode) {
                console.log("Updating account type", formData);
                let accType = await API.put(BUSINESS_ACCOUNT_TYPES_URL, {
                    id: formData.id,
                    accountType: formData.accountType,
                    accountTypeDisplayName: formData.accountTypeDisplayName,
                });
            } else {
                console.log("Add new account type");
                let accType = await API.post(BUSINESS_ACCOUNT_TYPES_URL, {
                    accountType: formData.accountType,
                    accountTypeDisplayName: formData.accountTypeDisplayName,
                });
            }
            setFormData({
                initialState,
                infoMessage: "Account type was saved successfully"
            });

        } catch (error) {
            let exError = extractError(error);
            setFormData({
                ...formData,
                errors: exError

            })
        }

        fetchAccountTypes();
    }

    const handleChange = async (event) => {
        if (formData.readOnly) {
            return;
        }
        const name = event.target.name;
        const value = event.target.value;
        setFormData({
            ...formData,
            [name]: value
        })

    }

    const handleValidation = async (event) => {
        const name = event.target.name;
        const value = event.target.value;
        let { hasError, error } = validateInput(name, value);
        setFormValidityData({
            ...formValidityData,
            [name + 'Error']: hasError,
            [name + 'ErrMsg']: error
        })        
    }

    const deleteAccountType = async (event, accountTypeId) => {
        try {
            await API.delete(BUSINESS_ACCOUNT_TYPES_URL + "/" + accountTypeId);
            setFormData({
                initialState,
                infoMessage: "Account type was deleted successfully"
            });

        } catch (error) {
            let exError = extractError(error);
            setFormData({
                initialState,
                errors: exError
            })
        }
        fetchAccountTypes();        
    }

    const editAccountType = async (event, donationCause) => {
        setFormData({
            ...donationCause,
            editMode: true
        });
        setFormValidityData(initialValidationState)
    }

    const handleCancel = async (event) => {
        setFormData(initialState);
        setFormValidityData(initialValidationState)        
    }

    const validateInput = (name, value) => {
        let hasError = false,
        error = ""
    switch (name) {
        case "accountType":
            if (value.trim() === "") {
                hasError = true
                error = "Account type cannot be empty"
            }  else if (!containsAlphaNumOnly(value)) {
                hasError = true
                error = "Account type code can only have alpha-numeric characters"
            }  else if (doesStringHaveSpecialChars(value)) {
                hasError = true
                error = "Account type code can only have alpha-numeric characters"
            } else if (value.length < 5) {
                hasError = true
                error = "Account type  must be greater than 5 characters"
            }
            else {
                hasError = false
                error = ""
            }
            break;
        case "accountTypeDisplayName":
            if (value.trim() === "") {
                hasError = true
                error = "Account type display name cannot be empty"
            } else if (!containsAlphaNumAndSpaceOnly(value)) {
                hasError = true
                error = "Account type display name can only have alpha-numeric characters"
            } else if (doesStringHaveSpecialChars(value)) {
                hasError = true
                error = "Account type display name can only have alpha-numeric characters. no special"
            } else if (value.length < 5) {
                hasError = true
                error = "Account type display name  must be greater than 5 characters"
            }
            else {
                hasError = false
                error = ""
            }
            break;
            default:
                break;
        }
        return { hasError, error };
    }

  return (
    <>
        <FormContainer title="Account Types" submitButtonFunction={handleSubmit}
            errors={formData.errors} infoMessage={formData.infoMessage} toastCloseFunc={onToastClose}>
            <Row>
                <Col mb={4} lg={4} sm={12}>
                    <FormField name="accountTypeDisplayName" label="Display Name" type="text" value={formData.accountTypeDisplayName}
                        placeholder="Enter account type display name" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.accountTypeDisplayNameError} valMsg={formValidityData.accountTypeDisplayNameErrMsg}
                        readOnly={formData.readOnly} />
                </Col>
                <Col mb={4} lg={4} sm={12}>
                    <FormField name="accountType" label="Account Type Code" type="text" value={formData.accountType}
                        placeholder="Account Type Code" onChange={handleChange} onBlur={handleValidation}
                        valstate={!formValidityData.accountTypeError} valMsg={formValidityData.accountTypeErrMsg}
                        readOnly={formData.editMode} />
                </Col>
            </Row>
            <Row>
                <Col>
                    <SubmitButton label='Save Account Type' disabled={formData.readOnly} className="me-4" />
                    <CancelButton label='Cancel' onClick={handleCancel} />
                </Col>
            </Row>

        </FormContainer>
        <Card>
            <Card.Body>
                <Table className="table-hover table-striped">
                    <thead>
                        <tr>
                            <th className="border-0">Account Type Code</th>
                            <th className="border-0">Account Type Display Name</th>
                            <th className="border-0">Edit</th>
                            <th className="border-0">Delete</th>
                        </tr>

                    </thead>
                    <tbody>
                        {accountTypes && !_.isEmpty(accountTypes) && accountTypes.map((accountType, idx) => (
                            <tr key={idx}>
                                <td>{accountType.accountType}</td>
                                <td>{accountType.accountTypeDisplayName}</td>
                                <td>{accountType.accountType !== 'DONATION' && <Button variant="dark" className="border-0 text-dark" onClick={evt => editAccountType(evt, accountType)}>
                                    <FontAwesomeIcon icon={faEdit} />
                                </Button>}</td>
                                <td>{accountType.accountType !== 'DONATION' && <Button variant="dark" className="border-0 text-dark" onClick={evt => deleteAccountType(evt, accountType.id)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Card.Body>
        </Card>
    </>
  )
}

export default BusinessAccountTypesPage